/*
  One Tooth World — wishlist (PRD §6.9). Loaded globally as a module.
  Stores product handles in localStorage 'otw_wishlist'. Hearts: [data-otw-wishlist-toggle="handle"].
  Emits 'wishlist:change'. Also drives the /pages/wishlist section ([data-otw-wishlist-page]).
*/
const OTW = (window.OTW = window.OTW || {});
const KEY = 'otw_wishlist';
const t = (key, fallback) => (OTW.strings && OTW.strings[key]) || fallback;
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const wishlistUrl = () => `${root()}pages/wishlist`;

function read() {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable (private mode / quota): state stays in memory for this page */
    memory = list;
  }
}

let memory = null;
const get = () => {
  const stored = read();
  return memory && !stored.length ? memory : stored;
};

function syncHearts(scope = document) {
  const list = get();
  scope.querySelectorAll('[data-otw-wishlist-toggle]').forEach((btn) => {
    const on = list.includes(btn.dataset.otwWishlistToggle);
    btn.setAttribute('aria-pressed', String(on));
    btn.classList.toggle('is-active', on);
    const label = on ? btn.dataset.labelRemove : btn.dataset.labelAdd;
    if (label) btn.setAttribute('aria-label', label);
  });
}

function changed(silent) {
  const list = get();
  syncHearts();
  OTW.updateBubbles?.('wishlist', list.length);
  if (!silent) OTW.emit('wishlist:change', { handles: list });
}

OTW.wishlist = {
  get,
  count: () => get().length,
  has: (handle) => get().includes(handle),
  add(handle) {
    const list = get();
    if (!list.includes(handle)) write([...list, handle]);
    changed();
  },
  remove(handle) {
    write(get().filter((h) => h !== handle));
    changed();
  },
  toggle(handle, button) {
    const adding = !OTW.wishlist.has(handle);
    if (adding) OTW.wishlist.add(handle);
    else OTW.wishlist.remove(handle);
    if (button) {
      button.classList.remove('is-pop');
      void button.offsetWidth;
      if (adding && !OTW.reducedMotion?.()) button.classList.add('is-pop');
    }
    OTW.toast?.(adding ? t('wishlistAdded', 'Added to wishlist') : t('wishlistRemoved', 'Removed from wishlist'), {
      action: adding ? t('view', 'View') : undefined,
      href: adding ? wishlistUrl() : undefined,
      icon: adding,
    });
    return adding;
  },
};

document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-otw-wishlist-toggle]');
  if (!btn) return;
  event.preventDefault();
  event.stopPropagation();
  OTW.wishlist.toggle(btn.dataset.otwWishlistToggle, btn);
});

document.addEventListener('animationend', (event) => {
  if (event.target.closest?.('.is-pop')) event.target.closest('.is-pop').classList.remove('is-pop');
});

/* Cross-tab sync */
window.addEventListener('storage', (event) => {
  if (event.key === KEY || event.key === null) changed();
});

/* Hearts added later (fetched sections, sliders) pick up their state */
let pending = false;
new MutationObserver((mutations) => {
  if (pending || !mutations.some((m) => m.addedNodes.length)) return;
  pending = true;
  requestAnimationFrame(() => {
    pending = false;
    syncHearts();
  });
}).observe(document.documentElement, { childList: true, subtree: true });

/* ---------- /pages/wishlist ---------- */
async function initPage(page) {
  if (!page || page.dataset.ready) return;
  page.dataset.ready = 'true';
  const grid = page.querySelector('[data-otw-wishlist-grid]');
  const empty = page.querySelector('[data-otw-wishlist-empty]');
  const actions = page.querySelector('[data-otw-wishlist-actions]');
  const skeleton = page.querySelector('[data-otw-wishlist-skeleton]');
  const moveAll = page.querySelector('[data-otw-wishlist-move-all]');

  const showState = () => {
    const has = grid.children.length > 0;
    empty.hidden = has;
    actions.hidden = !has;
  };

  const load = async () => {
    const handles = get();
    grid.innerHTML = '';
    if (!handles.length) {
      skeleton.hidden = true;
      showState();
      return;
    }
    skeleton.hidden = false;
    const results = await Promise.all(
      handles.map((handle) =>
        OTW.fetchSection(`${root()}products/${handle}`, 'otw-product-card-render')
          .then((el) => ({ handle, html: el && el.querySelector('[data-otw-card]') ? el.innerHTML : null }))
          .catch(() => ({ handle, html: null }))
      )
    );
    skeleton.hidden = true;
    results.forEach(({ handle, html }) => {
      if (!html) return;
      const item = document.createElement('li');
      item.className = 'otw-wishlist__item';
      item.dataset.handle = handle;
      item.innerHTML = html;
      grid.appendChild(item);
    });
    syncHearts(grid);
    showState();
  };

  OTW.on('wishlist:change', ({ handles }) => {
    grid.querySelectorAll('[data-handle]').forEach((item) => {
      if (!handles.includes(item.dataset.handle)) item.remove();
    });
    if (handles.some((h) => !grid.querySelector(`[data-handle="${CSS.escape(h)}"]`))) load();
    else showState();
  });

  moveAll?.addEventListener('click', async () => {
    const buttons = [...grid.querySelectorAll('[data-otw-add-to-cart]')];
    if (!buttons.length) {
      OTW.toast?.(t('wishlistChoose', 'Some items need an option choice. Use quick view to add them.'), { icon: false });
      return;
    }
    const items = buttons.map((b) => ({ id: Number(b.dataset.variantId), quantity: 1 }));
    const ok = await OTW.cart.addMany(items, moveAll);
    if (!ok) return;
    buttons.forEach((b) => OTW.wishlist.remove(b.closest('[data-otw-card]').dataset.productHandle));
    OTW.toast?.(t('wishlistMoved', '[count] added to cart').replace('[count]', items.length));
    if (grid.querySelector('[data-otw-quick-view]:not(.otw-card__eye)')) {
      OTW.toast?.(t('wishlistChoose', 'Some items need an option choice. Use quick view to add them.'), { icon: false });
    }
  });

  load();
}

const boot = () => initPage(document.querySelector('[data-otw-wishlist-page]'));
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
document.addEventListener('shopify:section:load', boot);

changed(true);
